import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../styles/MembersScreen.scss";
import {
  getAuthHeaders,
  getAvatarFromCache,
  getEffectiveAccessLevel,
  getProfile,
  isAdmin,
  isLeader,
  normalizeAccessLevel,
  stripAccessFromRole,
} from "../utils/auth";
import { apiFetch } from "../utils/api";
import {
  buildTechnologyCatalogMeta,
  getTechnologyVisual,
} from "../utils/technologies";
import { formatPresenceStatus } from "../utils/presence";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";
const SQLITE_DATETIME_RE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

const getInitials = (value) => {
  if (!value) {
    return "У";
  }
  const parts = value.trim().split(" ").filter(Boolean);
  if (parts.length === 0) {
    return "У";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const normalizeUsername = (value = "") =>
  value.toString().trim().replace(/^@/, "").toLowerCase();

const normalizeGithubProfileUrl = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  let candidate = raw;
  if (!/^https?:\/\//i.test(candidate)) {
    candidate = `https://${candidate}`;
  }

  let parsed;
  try {
    parsed = new URL(candidate);
  } catch (error) {
    return "";
  }

  const host = parsed.hostname.toLowerCase();
  if (host !== "github.com" && host !== "www.github.com") {
    return "";
  }

  const normalizedPath = parsed.pathname.replace(/\/+$/, "");
  if (!normalizedPath || normalizedPath === "/") {
    return "";
  }

  return `https://github.com${normalizedPath}`;
};

const getMemberDisplayName = (user) =>
  user.full_name ||
  [user.first_name, user.last_name].filter(Boolean).join(" ") ||
  user.username ||
  "Без имени";

const getLastSeenTimestamp = (value) => {
  if (!value) {
    return null;
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  const normalized = SQLITE_DATETIME_RE.test(raw)
    ? `${raw.replace(" ", "T")}Z`
    : raw;
  const timestamp = Date.parse(normalized);

  if (Number.isNaN(timestamp)) {
    return null;
  }

  return timestamp;
};

const compareMembersAlphabetically = (first, second) => {
  const nameCompare = getMemberDisplayName(first).localeCompare(
    getMemberDisplayName(second),
    "ru",
    { sensitivity: "base" }
  );
  if (nameCompare !== 0) {
    return nameCompare;
  }

  return String(first.username || "").localeCompare(
    String(second.username || ""),
    "ru",
    { sensitivity: "base" }
  );
};

const compareMembersByPresence = (first, second) => {
  const firstOnline = Boolean(first.is_online);
  const secondOnline = Boolean(second.is_online);

  if (firstOnline !== secondOnline) {
    return firstOnline ? -1 : 1;
  }

  if (firstOnline && secondOnline) {
    return compareMembersAlphabetically(first, second);
  }

  const firstLastSeen = getLastSeenTimestamp(first.last_seen);
  const secondLastSeen = getLastSeenTimestamp(second.last_seen);
  const firstHasLastSeen = firstLastSeen !== null;
  const secondHasLastSeen = secondLastSeen !== null;

  if (firstHasLastSeen !== secondHasLastSeen) {
    return firstHasLastSeen ? -1 : 1;
  }

  if (firstHasLastSeen && secondHasLastSeen && firstLastSeen !== secondLastSeen) {
    return secondLastSeen - firstLastSeen;
  }

  return compareMembersAlphabetically(first, second);
};

const createInitialForm = () => ({
  telegram_id: "",
  full_name: "",
  username: "",
  role: "Участник",
  date_of_birthday: "",
  number_of_phone: "",
  may_to_open: true,
  access_level: "member",
  helper_project_id: "",
  github_url: "",
  technologies: "",
  achievements: "",
});

const splitMemberList = (value) =>
  String(value || "")
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter((item, index, items) =>
      item && items.findIndex((candidate) => candidate.toLowerCase() === item.toLowerCase()) === index
    );

export default function MembersScreen() {
  const profile = useMemo(() => getProfile(), []);
  const [effectiveAccessLevel, setEffectiveAccessLevel] = useState(
    getEffectiveAccessLevel(profile)
  );
  const canEdit = isAdmin(effectiveAccessLevel);
  const [users, setUsers] = useState([]);
  const [membersSortMode, setMembersSortMode] = useState("presence");
  const [usersError, setUsersError] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [projects, setProjects] = useState([]);
  const [projectsError, setProjectsError] = useState("");
  const [editForm, setEditForm] = useState({});
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [activeMemberId, setActiveMemberId] = useState("");
  const [infoMemberId, setInfoMemberId] = useState("");
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState({
    telegramId: "",
    label: "",
  });
  const [pickerSearchTerm, setPickerSearchTerm] = useState("");
  const [remoteSearchUsers, setRemoteSearchUsers] = useState(null);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [searchResultsVersion, setSearchResultsVersion] = useState(0);
  const [searchUsersError, setSearchUsersError] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  const [membersActionError, setMembersActionError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createForm, setCreateForm] = useState(() => createInitialForm());
  const [technologyCatalog, setTechnologyCatalog] = useState({
    types: [],
    technologies: [],
  });
  const closeTimerRef = useState({ current: null })[0];
  const searchRequestRef = useRef(0);

  const applyUsersList = useCallback((data) => {
    const list = Array.isArray(data) ? data : [];
    setUsers(list);

    const profileId = profile?.telegram_id;
    const profileUsername = profile?.username?.replace(/^@/, "").toLowerCase();
    const matchedUser = list.find(
      (user) =>
        (profileId && user.telegram_id === profileId) ||
        (profileUsername && user.username?.toLowerCase() === profileUsername)
    );
    if (matchedUser) {
      setEffectiveAccessLevel(
        normalizeAccessLevel(matchedUser.level_of_access || "", matchedUser.role || "")
      );
    }
  }, [profile]);

  const refreshUsers = () =>
    apiFetch(`${API_BASE}/get_users`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        applyUsersList(data);
        return data;
      });

  const refreshProjects = () => {
    if (!canEdit) {
      return Promise.resolve([]);
    }
    return apiFetch(`${API_BASE}/projects`, { headers: getAuthHeaders() })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setProjects(list);
        return list;
      });
  };

  const syncUsersAndProjects = () =>
    refreshUsers().then(() => refreshProjects());

  const upsertHelperMembership = (
    helperProjectId,
    normalizedUsername,
    fullName,
    telegramId,
    projectList
  ) => {
    if (!helperProjectId || !normalizedUsername || projectList.length === 0) {
      return Promise.resolve();
    }

    const projectId = Number(helperProjectId);
    if (Number.isNaN(projectId)) {
      return Promise.resolve();
    }

    const project = projectList.find((entry) => Number(entry.id) === projectId);
    const members = Array.isArray(project?.members) ? project.members : [];
    const isMember = members.some(
      (member) => normalizeUsername(member.username || "") === normalizedUsername
    );

    const request = isMember
      ? apiFetch(`${API_BASE}/projects/${projectId}/members/${normalizedUsername}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ role: "Хелпер" }),
        })
      : apiFetch(`${API_BASE}/projects/${projectId}/members`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            username: normalizedUsername,
            full_name: fullName || "",
            role: "Хелпер",
            telegram_id: String(telegramId || ""),
          }),
        });

    return request.then((response) => {
      if (!response.ok) {
        throw new Error("failed");
      }
    });
  };

  useEffect(() => {
    let isActive = true;
    setIsLoadingUsers(true);
    setUsersError("");

    apiFetch(`${API_BASE}/get_users`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        if (!isActive) {
          return;
        }
        applyUsersList(data);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setUsersError("Не удалось загрузить пользователей");
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setIsLoadingUsers(false);
      });

    return () => {
      isActive = false;
    };
  }, [applyUsersList]);

  useEffect(() => {
    if (!canEdit) {
      return;
    }

    let isActive = true;
    setProjectsError("");

    apiFetch(`${API_BASE}/projects`, { headers: getAuthHeaders() })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        if (!isActive) {
          return;
        }
        setProjects(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setProjectsError("Не удалось загрузить проекты");
      });

    return () => {
      isActive = false;
    };
  }, [canEdit]);

  useEffect(() => {
    let isActive = true;

    apiFetch(`${API_BASE}/technologies`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        if (!isActive) {
          return;
        }
        setTechnologyCatalog({
          types: Array.isArray(data?.types) ? data.types : [],
          technologies: Array.isArray(data?.technologies) ? data.technologies : [],
        });
      })
      .catch(() => {});

    return () => {
      isActive = false;
    };
  }, []);

  const helperProjectByKey = useMemo(() => {
    const mapping = {};
    projects.forEach((project) => {
      const members = Array.isArray(project.members) ? project.members : [];
      members.forEach((member) => {
        if (!isLeader(member.role || "")) {
          return;
        }
        if (member.telegram_id) {
          mapping[String(member.telegram_id)] = project.id;
        }
        const normalized = normalizeUsername(member.username || "");
        if (normalized) {
          mapping[normalized] = project.id;
        }
      });
    });
    return mapping;
  }, [projects]);

  const technologyCatalogMeta = useMemo(
    () => buildTechnologyCatalogMeta(technologyCatalog),
    [technologyCatalog],
  );

  const normalizedPickerSearchTerm = pickerSearchTerm.trim().toLowerCase();

  const pickerUsers = useMemo(() => {
    if (!normalizedPickerSearchTerm) {
      return users;
    }
    if (!Array.isArray(remoteSearchUsers)) {
      return [];
    }
    return remoteSearchUsers;
  }, [normalizedPickerSearchTerm, remoteSearchUsers, users]);

  const isSearchingResults = Boolean(normalizedPickerSearchTerm) && isSearchingUsers;
  const isShowingSearchResults = Boolean(normalizedPickerSearchTerm) && !isSearchingResults;

  const pickerListHeight = useMemo(() => {
    if (isSearchingResults) {
      return 136;
    }

    if (pickerUsers.length === 0) {
      return 92;
    }

    const visibleRows = Math.min(5, pickerUsers.length);
    const rowHeight = 62;
    const rowGap = 10;
    const totalHeight =
      visibleRows * rowHeight + Math.max(0, visibleRows - 1) * rowGap + 6;

    return Math.min(308, Math.max(92, totalHeight));
  }, [isSearchingResults, pickerUsers.length]);

  const sortedUsers = useMemo(() => {
    const comparator =
      membersSortMode === "alphabet"
        ? compareMembersAlphabetically
        : compareMembersByPresence;

    return [...users].sort(comparator);
  }, [membersSortMode, users]);

  useEffect(() => {
    if (!isPickerOpen) {
      return;
    }

    const term = pickerSearchTerm.trim();
    searchRequestRef.current += 1;
    const requestId = searchRequestRef.current;
    setSearchUsersError("");

    if (!term) {
      setRemoteSearchUsers(null);
      setIsSearchingUsers(false);
      setSearchResultsVersion(0);
      return;
    }

    setRemoteSearchUsers(null);
    setIsSearchingUsers(true);

    const timerId = setTimeout(() => {
      apiFetch(`${API_BASE}/users/search?term=${encodeURIComponent(term)}`, {
        headers: getAuthHeaders(),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("failed");
          }
          return response.json();
        })
        .then((data) => {
          if (searchRequestRef.current !== requestId) {
            return;
          }
          setRemoteSearchUsers(Array.isArray(data) ? data : []);
          setSearchResultsVersion((prev) => prev + 1);
        })
        .catch(() => {
          if (searchRequestRef.current !== requestId) {
            return;
          }
          setSearchUsersError("Не удалось выполнить поиск");
          setRemoteSearchUsers([]);
          setSearchResultsVersion((prev) => prev + 1);
        })
        .finally(() => {
          if (searchRequestRef.current !== requestId) {
            return;
          }
          setIsSearchingUsers(false);
        });
    }, 180);

    return () => {
      clearTimeout(timerId);
    };
  }, [isPickerOpen, pickerSearchTerm]);

  useEffect(() => {
    if (!canEdit) {
      return;
    }

    const nextForm = {};
    users.forEach((user) => {
      const roleValue = stripAccessFromRole(user.role || "");
      const accessLevel = normalizeAccessLevel(user.level_of_access || "", user.role || "");
      const helperProjectId =
        helperProjectByKey[String(user.telegram_id || "")] ||
        helperProjectByKey[normalizeUsername(user.username || "")] ||
        "";
      nextForm[user.telegram_id] = {
        telegram_id: String(user.telegram_id || ""),
        full_name: user.full_name || "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        photo_url: user.photo_url || "",
        username: user.username || "",
        role: roleValue,
        date_of_birthday: user.date_of_birthday || "",
        number_of_phone: user.number_of_phone || "",
        may_to_open: Boolean(user.may_to_open),
        access_level: accessLevel,
        helper_project_id: helperProjectId ? String(helperProjectId) : "",
        github_url: user.github_url || "",
        technologies: (Array.isArray(user.technologies) ? user.technologies : []).join(", "),
        achievements: (Array.isArray(user.achievements) ? user.achievements : []).join("\n"),
      };
    });
    setEditForm(nextForm);
  }, [users, canEdit, helperProjectByKey]);

  const handleEditChange = (telegramId, field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [telegramId]: {
        ...prev[telegramId],
        [field]: value,
      },
    }));
  };

  const handleCreateChange = (field, value) => {
    setCreateForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = (telegramId) => {
    if (isSubmitting) {
      return;
    }

    const payload = editForm[telegramId];
    if (!payload) {
      return;
    }

    const roleValue = stripAccessFromRole(payload.role || "");
    const accessLevel = normalizeAccessLevel(payload.access_level || "", roleValue);
    const helperProjectId = payload.helper_project_id || "";
    const normalizedUsername = normalizeUsername(payload.username || "");
    const nextTelegramId = String(payload.telegram_id || "").trim();

    if (!nextTelegramId) {
      setMembersActionError("Укажите Telegram ID");
      return;
    }

    setMembersActionError("");
    setIsSubmitting(true);

    apiFetch(`${API_BASE}/users/${telegramId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        telegram_id: nextTelegramId,
        full_name: payload.full_name,
        first_name: payload.first_name,
        last_name: payload.last_name,
        photo_url: payload.photo_url,
        username: normalizedUsername,
        role: roleValue,
        date_of_birthday: payload.date_of_birthday,
        number_of_phone: payload.number_of_phone,
        may_to_open: payload.may_to_open,
        level_of_access: accessLevel,
        github_url: payload.github_url,
        technologies: splitMemberList(payload.technologies),
        achievements: splitMemberList(payload.achievements),
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
      })
      .then(() => refreshProjects())
      .then((updatedProjects) =>
        upsertHelperMembership(
          helperProjectId,
          normalizedUsername,
          payload.full_name,
          nextTelegramId,
          updatedProjects
        )
      )
      .then(() => syncUsersAndProjects())
      .then(() => closeOverlays())
      .catch(() => setMembersActionError("Не удалось обновить пользователя"))
      .finally(() => setIsSubmitting(false));
  };

  const handleCreateMember = (event) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    const telegramId = String(createForm.telegram_id || "").trim();
    const username = normalizeUsername(createForm.username || "");
    const roleValue = stripAccessFromRole(createForm.role || "");
    const accessLevel = normalizeAccessLevel(createForm.access_level || "", roleValue);

    if (!telegramId) {
      setMembersActionError("Укажите telegram id");
      return;
    }

    setMembersActionError("");
    setIsSubmitting(true);

    apiFetch(`${API_BASE}/users`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        telegram_id: telegramId,
        full_name: (createForm.full_name || "").trim(),
        username,
        role: roleValue,
        date_of_birthday: (createForm.date_of_birthday || "").trim(),
        number_of_phone: (createForm.number_of_phone || "").trim(),
        may_to_open: Boolean(createForm.may_to_open),
        level_of_access: accessLevel,
        github_url: (createForm.github_url || "").trim(),
        technologies: splitMemberList(createForm.technologies),
        achievements: splitMemberList(createForm.achievements),
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
      })
      .then(() =>
        upsertHelperMembership(
          createForm.helper_project_id,
          username,
          createForm.full_name,
          telegramId,
          projects
        )
      )
      .then(() => syncUsersAndProjects())
      .then(() => {
        setCreateForm(createInitialForm());
        closeOverlays();
      })
      .catch(() => setMembersActionError("Не удалось добавить участника"))
      .finally(() => setIsSubmitting(false));
  };

  const handleDeleteMember = (telegramId) => {
    if (isSubmitting || !telegramId) {
      return;
    }

    const displayName =
      selectedMember?.full_name ||
      selectedMember?.username ||
      "этого участника";
    setMembersActionError("");
    setDeleteCandidate({
      telegramId: String(telegramId),
      label: displayName,
    });
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteMemberClose = () => {
    if (isSubmitting) {
      return;
    }
    setIsDeleteConfirmOpen(false);
    setDeleteCandidate({ telegramId: "", label: "" });
  };

  const handleDeleteMemberConfirm = () => {
    if (isSubmitting || !deleteCandidate.telegramId) {
      return;
    }

    setMembersActionError("");
    setIsSubmitting(true);

    apiFetch(`${API_BASE}/users/${deleteCandidate.telegramId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
      })
      .then(() => syncUsersAndProjects())
      .then(() => closeOverlays())
      .catch(() => setMembersActionError("Не удалось удалить участника"))
      .finally(() => setIsSubmitting(false));
  };

  const selectedMember = useMemo(
    () =>
      users.find(
        (user) =>
          String(user.telegram_id || "") === String(activeMemberId || "")
      ),
    [users, activeMemberId]
  );

  const infoMember = useMemo(
    () =>
      users.find(
        (user) => String(user.telegram_id || "") === String(infoMemberId || "")
      ),
    [users, infoMemberId]
  );

  const selectedValues = editForm[activeMemberId] || {};

  const closeOverlays = useCallback(() => {
    if (isClosing) {
      return;
    }
    setIsClosing(true);
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      searchRequestRef.current += 1;
      setIsPickerOpen(false);
      setIsEditorOpen(false);
      setIsCreateOpen(false);
      setIsDeleteConfirmOpen(false);
      setDeleteCandidate({ telegramId: "", label: "" });
      setActiveMemberId("");
      setInfoMemberId("");
      setPickerSearchTerm("");
      setRemoteSearchUsers(null);
      setIsSearchingUsers(false);
      setSearchResultsVersion(0);
      setSearchUsersError("");
      setMembersActionError("");
      setCreateForm(createInitialForm());
      setIsClosing(false);
      closeTimerRef.current = null;
    }, 260);
  }, [closeTimerRef, isClosing]);

  const openPicker = () => {
    if (!canEdit) {
      return;
    }
    setIsClosing(false);
    setIsCreateOpen(false);
    setIsEditorOpen(false);
    setIsDeleteConfirmOpen(false);
    setInfoMemberId("");
    setActiveMemberId("");
    searchRequestRef.current += 1;
    setPickerSearchTerm("");
    setRemoteSearchUsers(null);
    setIsSearchingUsers(false);
    setSearchResultsVersion(0);
    setSearchUsersError("");
    setMembersActionError("");
    setIsPickerOpen(true);
  };

  const openCreate = () => {
    if (!canEdit) {
      return;
    }
    setIsClosing(false);
    setIsPickerOpen(false);
    setIsEditorOpen(false);
    setIsDeleteConfirmOpen(false);
    setInfoMemberId("");
    setActiveMemberId("");
    searchRequestRef.current += 1;
    setPickerSearchTerm("");
    setRemoteSearchUsers(null);
    setIsSearchingUsers(false);
    setSearchResultsVersion(0);
    setSearchUsersError("");
    setMembersActionError("");
    setCreateForm(createInitialForm());
    setIsCreateOpen(true);
  };

  const openEditorForMember = (telegramId) => {
    setIsClosing(false);
    setIsCreateOpen(false);
    setIsDeleteConfirmOpen(false);
    setInfoMemberId("");
    searchRequestRef.current += 1;
    setPickerSearchTerm("");
    setRemoteSearchUsers(null);
    setIsSearchingUsers(false);
    setSearchResultsVersion(0);
    setSearchUsersError("");
    setMembersActionError("");
    setActiveMemberId(telegramId);
    setIsPickerOpen(false);
    setIsEditorOpen(true);
  };

  const openInfoForMember = (telegramId) => {
    setIsClosing(false);
    setInfoMemberId(telegramId);
  };

  useEffect(() => {
    if (
      !isPickerOpen &&
      !isEditorOpen &&
      !isCreateOpen &&
      !isDeleteConfirmOpen &&
      !infoMemberId
    ) {
      return;
    }
    const handleKeydown = (event) => {
      if (event.key === "Escape") {
        closeOverlays();
      }
    };
    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [
    closeOverlays,
    isPickerOpen,
    isEditorOpen,
    isCreateOpen,
    isDeleteConfirmOpen,
    infoMemberId,
  ]);

  const overlayContent =
    isPickerOpen || isEditorOpen || isCreateOpen || isDeleteConfirmOpen || infoMemberId ? (
      <div
        className={`members-screen__overlay${isClosing ? " is-closing" : ""}`}
        onClick={closeOverlays}
      >
        {infoMember &&
          !isPickerOpen &&
          !isEditorOpen &&
          !isCreateOpen &&
          !isDeleteConfirmOpen && (
          <div
            className={`members-modal members-modal--info${
              isClosing ? " is-closing" : ""
            }`}
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="members-modal__header">
              <div className="members-modal__hero">
                <div
                  className="members-modal__avatar"
                  style={{
                    "--hue": (() => {
                      const label =
                        infoMember.full_name ||
                        [infoMember.first_name, infoMember.last_name]
                          .filter(Boolean)
                          .join(" ") ||
                        infoMember.username ||
                        "Без имени";
                      return (label.charCodeAt(0) * 37) % 360;
                    })(),
                  }}
                >
                  {(() => {
                    const displayName =
                      infoMember.full_name ||
                      [infoMember.first_name, infoMember.last_name]
                        .filter(Boolean)
                        .join(" ") ||
                      infoMember.username ||
                      "Без имени";
                    const cachedAvatar = getAvatarFromCache({
                      telegramId: infoMember.telegram_id,
                      username: infoMember.username,
                    });
                    const avatar = infoMember.photo_url || cachedAvatar;
                    const initials = getInitials(displayName);
                    return avatar ? (
                      <img src={avatar} alt={displayName} loading="lazy" />
                    ) : (
                      <span>{initials}</span>
                    );
                  })()}
                </div>
                <div className="members-modal__profile-meta">
                  <p className="members-modal__name">
                    {infoMember.full_name ||
                      [infoMember.first_name, infoMember.last_name]
                        .filter(Boolean)
                        .join(" ") ||
                      "Без имени"}
                  </p>
                  <p className="members-modal__hint">
                    {infoMember.username
                      ? infoMember.username.startsWith("@")
                        ? infoMember.username
                        : `@${infoMember.username}`
                      : "—"}
                  </p>
                  <p
                    className={`members-modal__presence${
                      infoMember.is_online ? " is-online" : " is-offline"
                    }`}
                  >
                    {formatPresenceStatus(infoMember.is_online, infoMember.last_seen)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="members-modal__close"
                onClick={closeOverlays}
                aria-label="Закрыть"
              >
                <span />
                <span />
              </button>
            </div>
            <div className="members-modal__info">
            <div className="members-modal__details">
                <div className="members-modal__detail">
                  <span>роль</span>
                  <p>{stripAccessFromRole(infoMember.role || "") || "—"}</p>
                </div>
                <div className="members-modal__detail">
                  <span>телефон</span>
                  <p>{infoMember.number_of_phone || "—"}</p>
                </div>
                <div className="members-modal__detail">
                  <span>др</span>
                  <p>{infoMember.date_of_birthday || "—"}</p>
                </div>
                <div className="members-modal__detail">
                  <span>telegram id</span>
                  <p>{infoMember.telegram_id || "—"}</p>
                </div>
                <div className="members-modal__detail">
                  <span>github</span>
                  {normalizeGithubProfileUrl(infoMember.github_url) ? (
                    <p>
                      <a
                        className="members-modal__detail-link"
                        href={normalizeGithubProfileUrl(infoMember.github_url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {normalizeGithubProfileUrl(infoMember.github_url).replace(
                          /^https?:\/\//i,
                          "",
                        )}
                      </a>
                    </p>
                  ) : (
                    <p>—</p>
                  )}
                </div>
                <div className="members-modal__detail">
                  <span>доступ</span>
                  <p>
                    {infoMember.may_to_open
                      ? "доступ открыт"
                      : "доступ закрыт"}
                  </p>
                </div>
                <div className="members-modal__detail">
                  <span>онлайн</span>
                  <p>{formatPresenceStatus(infoMember.is_online, infoMember.last_seen)}</p>
                </div>
              </div>
              <div className="members-modal__technologies">
                <span className="members-modal__tech-title">Технологии</span>
                {Array.isArray(infoMember.technologies) &&
                infoMember.technologies.length > 0 ? (
                  <div className="members-modal__tech-list">
                    {infoMember.technologies.map((technology) => {
                      const visual = getTechnologyVisual(
                        technology,
                        technologyCatalogMeta,
                      );
                      return (
                        <span
                          className="members-tech-badge"
                          key={`info-tech-${infoMember.telegram_id}-${technology}`}
                          style={{ "--tech-hue": visual.hue }}
                        >
                          <span className="members-tech-badge__icon">
                            {visual.icon}
                          </span>
                          <span>{technology}</span>
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <p className="members-modal__tech-empty">
                    Пользователь пока не добавил технологии.
                  </p>
                )}
              </div>
              <div className="members-modal__achievements">
                <span className="members-modal__tech-title">Достижения</span>
                {Array.isArray(infoMember.achievements) && infoMember.achievements.length > 0 ? (
                  <ul>
                    {infoMember.achievements.map((achievement, index) => (
                      <li key={`${infoMember.telegram_id}-achievement-${index}`}>{achievement}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="members-modal__tech-empty">Достижения пока не добавлены.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {isPickerOpen && (
          <div
            className={`members-modal members-modal--picker${
              isClosing ? " is-closing" : ""
            }`}
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="members-modal__header">
              <div>
                <p className="members-modal__eyebrow">Выбор участника</p>
                <h3 className="members-modal__title">Кого будем редактировать?</h3>
              </div>
              <div className="members-modal__header-actions">
                <button
                  type="button"
                  className="members-modal__add-button"
                  onClick={openCreate}
                  aria-label="Добавить участника"
                >
                  +
                </button>
                <button
                  type="button"
                  className="members-modal__close"
                  onClick={closeOverlays}
                  aria-label="Закрыть"
                >
                  <span />
                  <span />
                </button>
              </div>
            </div>
            <div className="members-modal__search">
              <input
                type="search"
                value={pickerSearchTerm}
                onChange={(event) => setPickerSearchTerm(event.target.value)}
                placeholder="Поиск по имени, @username или Telegram ID"
                aria-label="Поиск участника"
                autoFocus
              />
            </div>
            {searchUsersError && (
              <p className="members-modal__error">{searchUsersError}</p>
            )}
            <div
              className={`members-modal__list${isSearchingResults ? " is-loading" : ""}${
                !isSearchingResults && pickerUsers.length === 0 ? " is-empty" : ""
              }`}
              style={{ "--picker-list-height": `${pickerListHeight}px` }}
            >
              {isSearchingResults ? (
                <div className="members-modal__loader-wrap" role="status" aria-live="polite">
                  <span className="members-modal__loader" />
                  <span className="members-modal__loader-text">Ищем варианты...</span>
                </div>
              ) : pickerUsers.length === 0 ? (
                <p className="members-modal__empty">
                  {normalizedPickerSearchTerm
                    ? "Ничего не найдено по вашему запросу"
                    : "Пользователи пока не добавлены"}
                </p>
              ) : (
                pickerUsers.map((user, index) => {
                  const displayName =
                    user.full_name ||
                    [user.first_name, user.last_name].filter(Boolean).join(" ") ||
                    user.username ||
                    "Без имени";
                  const username = user.username
                    ? user.username.startsWith("@")
                      ? user.username
                      : `@${user.username}`
                    : "—";
                  const cachedAvatar = getAvatarFromCache({
                    telegramId: user.telegram_id,
                    username: user.username,
                  });
                  const avatar = user.photo_url || cachedAvatar;
                  const initials = getInitials(displayName);
                  const hue = (displayName.charCodeAt(0) * 37) % 360;

                  return (
                    <button
                      type="button"
                      key={
                        isShowingSearchResults
                          ? `search-${searchResultsVersion}-${user.telegram_id || index}`
                          : `base-${user.telegram_id || index}`
                      }
                      className={`members-modal__item${
                        isShowingSearchResults ? " is-search-result" : ""
                      }`}
                      style={{ "--delay": `${index * 60}ms` }}
                      onClick={() => openEditorForMember(user.telegram_id)}
                    >
                      <span className="members-modal__avatar" style={{ "--hue": hue }}>
                        {avatar ? (
                          <img src={avatar} alt={displayName} loading="lazy" />
                        ) : (
                          <span>{initials}</span>
                        )}
                      </span>
                      <span className="members-modal__meta">
                        <span className="members-modal__name">{displayName}</span>
                        <span className="members-modal__hint">{username}</span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {isCreateOpen && (
          <div
            className={`members-modal members-modal--create${
              isClosing ? " is-closing" : ""
            }`}
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="members-modal__header">
              <div>
                <p className="members-modal__eyebrow">Новый участник</p>
                <h3 className="members-modal__title">Добавить пользователя</h3>
              </div>
              <button
                type="button"
                className="members-modal__close"
                onClick={closeOverlays}
                aria-label="Закрыть"
              >
                <span />
                <span />
              </button>
            </div>

            <form onSubmit={handleCreateMember}>
              <div className="members-modal__form">
                <label>
                  Telegram ID
                  <input
                    value={createForm.telegram_id}
                    onChange={(event) =>
                      handleCreateChange("telegram_id", event.target.value)
                    }
                    placeholder="1108856135"
                    required
                  />
                </label>
                <label>
                  ФИО
                  <input
                    value={createForm.full_name}
                    onChange={(event) =>
                      handleCreateChange("full_name", event.target.value)
                    }
                    placeholder="Иван Иванов"
                  />
                </label>
                <label>
                  Username
                  <input
                    value={createForm.username}
                    onChange={(event) =>
                      handleCreateChange("username", event.target.value)
                    }
                    placeholder="@username"
                  />
                </label>
                <label>
                  Роли
                  <input
                    value={createForm.role}
                    onChange={(event) => handleCreateChange("role", event.target.value)}
                  />
                </label>
                <label>
                  Телефон
                  <input
                    value={createForm.number_of_phone}
                    onChange={(event) =>
                      handleCreateChange("number_of_phone", event.target.value)
                    }
                  />
                </label>
                <label>
                  Дата рождения
                  <input
                    value={createForm.date_of_birthday}
                    onChange={(event) =>
                      handleCreateChange("date_of_birthday", event.target.value)
                    }
                  />
                </label>
                <label className="members-modal__form--full">
                  GitHub
                  <input value={createForm.github_url} onChange={(event) => handleCreateChange("github_url", event.target.value)} placeholder="https://github.com/username" />
                </label>
                <label className="members-modal__form--full">
                  Стек
                  <textarea value={createForm.technologies} onChange={(event) => handleCreateChange("technologies", event.target.value)} placeholder="React, Go, PostgreSQL" />
                </label>
                <label className="members-modal__form--full">
                  Достижения
                  <textarea value={createForm.achievements} onChange={(event) => handleCreateChange("achievements", event.target.value)} placeholder="Каждое достижение — с новой строки" />
                </label>
                <label className="members-modal__form--full">
                  Уровень доступа
                  <select
                    value={createForm.access_level}
                    onChange={(event) =>
                      handleCreateChange("access_level", event.target.value)
                    }
                  >
                    <option value="member">Участник</option>
                    <option value="moderator">Модератор</option>
                    <option value="admin">Админ</option>
                  </select>
                </label>
                <label className="members-modal__form--full">
                  Проект хелпера
                  <select
                    value={createForm.helper_project_id}
                    onChange={(event) =>
                      handleCreateChange("helper_project_id", event.target.value)
                    }
                    disabled={projects.length === 0}
                  >
                    <option value="">—</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.title || `Проект ${project.id}`}
                      </option>
                    ))}
                  </select>
                  {projectsError && (
                    <span className="members-modal__hint">{projectsError}</span>
                  )}
                </label>
                <div className="members-modal__toggle">
                  <span>Доступ открыт</span>
                  <label className="members-modal__switch">
                    <input
                      type="checkbox"
                      checked={Boolean(createForm.may_to_open)}
                      onChange={(event) =>
                        handleCreateChange("may_to_open", event.target.checked)
                      }
                    />
                    <span className="members-modal__slider" />
                  </label>
                </div>
              </div>

              {membersActionError && (
                <p className="members-modal__error">{membersActionError}</p>
              )}

              <div className="members-modal__actions">
                <button
                  type="submit"
                  className="members-modal__primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Сохранение..." : "Добавить"}
                </button>
                <button
                  type="button"
                  className="members-modal__ghost"
                  onClick={closeOverlays}
                  disabled={isSubmitting}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        )}

        {isDeleteConfirmOpen && (
          <div
            className={`members-modal members-modal--confirm${
              isClosing ? " is-closing" : ""
            }`}
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="members-modal__eyebrow members-modal__eyebrow--danger">
              Удаление участника
            </p>
            <h3 className="members-modal__title">Вы уверены?</h3>
            <p className="members-modal__confirm-text">
              {deleteCandidate.label
                ? `Участник «${deleteCandidate.label}» будет удален без возможности восстановления.`
                : "Пользователь будет удален без возможности восстановления."}
            </p>
            {membersActionError && (
              <p className="members-modal__error">{membersActionError}</p>
            )}
            <div className="members-modal__actions">
              <button
                type="button"
                className="members-modal__ghost"
                onClick={handleDeleteMemberClose}
                disabled={isSubmitting}
              >
                Отмена
              </button>
              <button
                type="button"
                className="members-modal__secondary"
                onClick={handleDeleteMemberConfirm}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Удаляем..." : "Удалить"}
              </button>
            </div>
          </div>
        )}

        {isEditorOpen && selectedMember && !isDeleteConfirmOpen && (
          <div
            className={`members-modal members-modal--editor${
              isClosing ? " is-closing" : ""
            }`}
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="members-modal__header">
              <div>
                <p className="members-modal__eyebrow">Редактирование</p>
                <h3 className="members-modal__title">
                  {selectedMember.full_name || selectedMember.username || "Участник"}
                </h3>
              </div>
              <button
                type="button"
                className="members-modal__close"
                onClick={closeOverlays}
                aria-label="Закрыть"
              >
                <span />
                <span />
              </button>
            </div>
            <div className="members-modal__form">
              <label>
                Telegram ID
                <input
                  value={selectedValues.telegram_id || ""}
                  onChange={(event) =>
                    handleEditChange(activeMemberId, "telegram_id", event.target.value)
                  }
                  inputMode="numeric"
                  autoComplete="off"
                />
              </label>
              <label>
                Telegram username
                <input
                  value={selectedValues.username || ""}
                  onChange={(event) =>
                    handleEditChange(activeMemberId, "username", event.target.value)
                  }
                  placeholder="@username"
                  autoComplete="off"
                />
              </label>
              <label>
                Имя
                <input value={selectedValues.first_name || ""} onChange={(event) => handleEditChange(activeMemberId, "first_name", event.target.value)} />
              </label>
              <label>
                Фамилия
                <input value={selectedValues.last_name || ""} onChange={(event) => handleEditChange(activeMemberId, "last_name", event.target.value)} />
              </label>
              <label>
                ФИО
                <input
                  value={selectedValues.full_name || ""}
                  onChange={(event) =>
                    handleEditChange(activeMemberId, "full_name", event.target.value)
                  }
                />
              </label>
              <label className="members-modal__form--full">
                Роли
                <input
                  value={selectedValues.role || ""}
                  onChange={(event) =>
                    handleEditChange(activeMemberId, "role", event.target.value)
                  }
                />
              </label>
              <label>
                Телефон
                <input
                  value={selectedValues.number_of_phone || ""}
                  onChange={(event) =>
                    handleEditChange(activeMemberId, "number_of_phone", event.target.value)
                  }
                />
              </label>
              <label>
                Дата рождения
                <input
                  value={selectedValues.date_of_birthday || ""}
                  onChange={(event) =>
                    handleEditChange(activeMemberId, "date_of_birthday", event.target.value)
                  }
                />
              </label>
              <label className="members-modal__form--full">
                GitHub
                <input
                  value={selectedValues.github_url || ""}
                  onChange={(event) => handleEditChange(activeMemberId, "github_url", event.target.value)}
                  placeholder="https://github.com/username"
                />
              </label>
              <label className="members-modal__form--full">
                Ссылка на фото
                <input
                  type="url"
                  value={selectedValues.photo_url || ""}
                  onChange={(event) => handleEditChange(activeMemberId, "photo_url", event.target.value)}
                />
              </label>
              <label className="members-modal__form--full">
                Стек
                <textarea
                  value={selectedValues.technologies || ""}
                  onChange={(event) => handleEditChange(activeMemberId, "technologies", event.target.value)}
                  placeholder="React, Go, PostgreSQL"
                />
              </label>
              <label className="members-modal__form--full">
                Достижения
                <textarea
                  value={selectedValues.achievements || ""}
                  onChange={(event) => handleEditChange(activeMemberId, "achievements", event.target.value)}
                  placeholder="Каждое достижение — с новой строки"
                />
              </label>
              <label className="members-modal__form--full">
                Уровень доступа
                <select
                  value={selectedValues.access_level || "member"}
                  onChange={(event) =>
                    handleEditChange(activeMemberId, "access_level", event.target.value)
                  }
                >
                  <option value="member">Участник</option>
                  <option value="moderator">Модератор</option>
                  <option value="admin">Админ</option>
                </select>
              </label>
              <label className="members-modal__form--full">
                Проект хелпера
                <select
                  value={selectedValues.helper_project_id || ""}
                  onChange={(event) =>
                    handleEditChange(activeMemberId, "helper_project_id", event.target.value)
                  }
                  disabled={projects.length === 0}
                >
                  <option value="">—</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.title || `Проект ${project.id}`}
                    </option>
                  ))}
                </select>
                {projectsError && (
                  <span className="members-modal__hint">{projectsError}</span>
                )}
              </label>
              <div className="members-modal__toggle">
                <span>Доступ открыт</span>
                <label className="members-modal__switch">
                  <input
                    type="checkbox"
                    checked={Boolean(selectedValues.may_to_open)}
                    onChange={(event) =>
                      handleEditChange(activeMemberId, "may_to_open", event.target.checked)
                    }
                  />
                  <span className="members-modal__slider" />
                </label>
              </div>
            </div>

            {membersActionError && (
              <p className="members-modal__error">{membersActionError}</p>
            )}

            <div className="members-modal__actions">
              <button
                type="button"
                className="members-modal__primary"
                onClick={() => handleSave(activeMemberId)}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Сохранение..." : "Сохранить"}
              </button>
              <button
                type="button"
                className="members-modal__secondary"
                onClick={() => handleDeleteMember(activeMemberId)}
                disabled={isSubmitting}
              >
                Удалить
              </button>
              <button
                type="button"
                className="members-modal__ghost"
                onClick={closeOverlays}
                disabled={isSubmitting}
              >
                Отмена
              </button>
            </div>
          </div>
        )}
      </div>
    ) : null;

  return (
    <section className="members-screen">
      <header className="members-screen__header">
        <h2 className="members-screen__title">Участники</h2>
        <div className="members-screen__actions">
          <button
            type="button"
            className={`members-screen__edit-button members-screen__sort-button${
              membersSortMode === "presence" ? " is-active" : ""
            }`}
            onClick={() =>
              setMembersSortMode((prev) =>
                prev === "presence" ? "alphabet" : "presence"
              )
            }
            title={
              membersSortMode === "presence"
                ? "Сейчас: онлайн сверху, затем по последнему входу. Нажмите для алфавитной сортировки."
                : "Сейчас: алфавитная сортировка. Нажмите для сортировки по онлайн/последнему входу."
            }
            aria-label="Переключить сортировку списка участников"
          >
            {membersSortMode === "presence" ? "Активность" : "Алфавит"}
          </button>
          {canEdit && (
            <button
              type="button"
              className="members-screen__edit-button"
              onClick={openPicker}
              disabled={isLoadingUsers}
            >
              Редактировать
            </button>
          )}
        </div>
      </header>

      <div className="members-screen__list">
        {isLoadingUsers && (
          <article className="member-card member-card--placeholder" aria-busy="true">
            <div className="member-card__main">
              <p className="member-card__name">Загрузка...</p>
              <p className="member-card__hint">подождите немного</p>
            </div>
          </article>
        )}

        {!isLoadingUsers && usersError && (
          <article className="member-card member-card--placeholder">
            <div className="member-card__main">
              <p className="member-card__name">Ошибка</p>
              <p className="member-card__hint">{usersError}</p>
            </div>
          </article>
        )}

        {!isLoadingUsers && !usersError && users.length === 0 && (
          <article className="member-card member-card--placeholder">
            <div className="member-card__main">
              <p className="member-card__name">Пользователи не найдены</p>
              <p className="member-card__hint">пока нет загруженных профилей</p>
            </div>
          </article>
        )}

        {!isLoadingUsers &&
          !usersError &&
          users.length > 0 &&
          sortedUsers.map((user, index) => {
            const displayName = getMemberDisplayName(user);
            const username = user.username
              ? user.username.startsWith("@")
                ? user.username
                : `@${user.username}`
              : "—";
            const role = stripAccessFromRole(user.role || "") || "—";
            const telegramId = user.telegram_id || "—";
            const accessLabel = user.may_to_open
              ? "доступ открыт"
              : "доступ закрыт";
            const cachedAvatar = getAvatarFromCache({
              telegramId: user.telegram_id,
              username: user.username,
            });
            const avatar = user.photo_url || cachedAvatar;
            const initials = getInitials(displayName);
            const hue = (displayName.charCodeAt(0) * 37) % 360;
            const presenceStatus = formatPresenceStatus(user.is_online, user.last_seen);
            return (
              <article
                className="member-card"
                key={telegramId}
                style={{ "--delay": `${index * 70}ms` }}
                role="button"
                tabIndex={0}
                onClick={() => openInfoForMember(String(telegramId))}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openInfoForMember(String(telegramId));
                  }
                }}
              >
                <div className="member-card__identity">
                  <div className="member-card__avatar" style={{ "--hue": hue }}>
                    {avatar ? (
                      <img src={avatar} alt={displayName} loading="lazy" />
                    ) : (
                      <span>{initials}</span>
                    )}
                  </div>
                  <div className="member-card__main">
                    <div className="member-card__topline">
                      <p className="member-card__name">{displayName}</p>
                      <p
                        className={`member-card__presence${
                          user.is_online ? " is-online" : " is-offline"
                        }`}
                      >
                        {presenceStatus}
                      </p>
                    </div>
                    <p className="member-card__hint">{username}</p>
                    <div className="member-card__tags">
                      <span className="member-card__tag">{role}</span>
                      <span className="member-card__tag is-muted">{accessLabel}</span>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
      </div>

      {overlayContent && createPortal(overlayContent, document.body)}
    </section>
  );
}
